import { Injectable, effect, signal, computed, WritableSignal } from '@angular/core';
import { Subject } from 'rxjs';
import { CookieConsentService } from '../../services/cookie-consent.service';

export interface layoutConfig {
    preset?: string;
    primary?: string;
    surface?: string | undefined | null;
    darkTheme?: boolean;
    menuMode?: string; // This will dynamically change
}

interface LayoutState {
    staticMenuDesktopInactive?: boolean;
    overlayMenuActive?: boolean;
    configSidebarVisible?: boolean;
    staticMenuMobileActive?: boolean;
    menuHoverActive?: boolean;
}

interface MenuChangeEvent {
    key: string;
    routeEvent?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class LayoutService {
    _config: layoutConfig = {
        preset: 'Aura',
        primary: 'emerald',
        surface: null,
        darkTheme: false,
        menuMode: 'static' // Default to static, but this can be overridden by user/logic
    };

    _state: LayoutState = {
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        configSidebarVisible: false,
        staticMenuMobileActive: false,
        menuHoverActive: false
    };

    layoutConfig: WritableSignal<layoutConfig> = signal<layoutConfig>(this._config);
    layoutState: WritableSignal<LayoutState> = signal<LayoutState>(this._state);

    private _currentIsDesktop: WritableSignal<boolean> = signal(false);
    private _forcedOverlayFromStaticDesktop: WritableSignal<boolean> = signal(false);

    private configUpdate = new Subject<layoutConfig>();
    private overlayOpen = new Subject<any>();
    private menuSource = new Subject<MenuChangeEvent>();
    private resetSource = new Subject();

    menuSource$ = this.menuSource.asObservable();
    resetSource$ = this.resetSource.asObservable();
    configUpdate$ = this.configUpdate.asObservable();
    overlayOpen$ = this.overlayOpen.asObservable();

    theme = computed(() => (this.layoutConfig()?.darkTheme ? 'light' : 'dark'));
    isSidebarActive = computed(() => this.layoutState().overlayMenuActive || this.layoutState().staticMenuMobileActive);
    isDarkTheme = computed(() => this.layoutConfig().darkTheme);
    getPrimary = computed(() => this.layoutConfig().primary);
    getSurface = computed(() => this.layoutConfig().surface);
    isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');
    transitionComplete = signal<boolean>(false);

    private initialized = false;
    private previousDarkTheme?: boolean;

    constructor(private cookieConsentService: CookieConsentService) 
    {
        this.loadConfigFromStorage();

        effect(() => {
            const config = this.layoutConfig();
            if (config && this.cookieConsentService.consentGiven()) {
                this.saveConfigToStorage(config);
                this.onConfigUpdate();
            }
        });

        effect(() => {
            const config = this.layoutConfig();
            if (!this.initialized || !config) {
                this.initialized = true;
                this.previousDarkTheme = config?.darkTheme;
                return;
            }
            // Only animate the dark/light view transition when darkTheme itself changed —
            // otherwise every color/preset change triggers a needless page-wide flicker.
            if (config.darkTheme === this.previousDarkTheme) {
                return;
            }
            this.previousDarkTheme = config.darkTheme;
            this.handleDarkModeTransition(config);
        });
    }

    private handleDarkModeTransition(config: layoutConfig): void {
        if ((document as any).startViewTransition) {
            this.startViewTransition(config);
        } else {
            this.toggleDarkMode(config);
            this.onTransitionEnd();
        }
    }
    private saveConfigToStorage(config: layoutConfig) 
    {
        if (!this.cookieConsentService.consentGiven()) return;
        localStorage.setItem('layoutConfig', JSON.stringify(config));
    }


    private loadConfigFromStorage() 
    {
        if (!this.cookieConsentService.consentGiven()) return;

        const storedConfig = localStorage.getItem('layoutConfig');
        if (storedConfig) {
            try {
                const parsed = JSON.parse(storedConfig) as layoutConfig;
                this.layoutConfig.set({
                    ...this._config,
                    ...parsed
                });    
            } 
            catch (e) {
                console.error('Failed to parse layoutConfig from storage:', e);
            }
        }
    }



    private startViewTransition(config: layoutConfig): void {
        const transition = (document as any).startViewTransition(() => {
            this.toggleDarkMode(config);
        });
        transition.ready
            .then(() => {
                this.onTransitionEnd();
            })
            .catch(() => {});
    }

    toggleDarkMode(config?: layoutConfig): void {
        const _config = config || this.layoutConfig();
        if (_config.darkTheme) 
        {
            document.documentElement.classList.add('app-dark');
        } else {
            document.documentElement.classList.remove('app-dark');
        }
    }

    private onTransitionEnd() {
        this.transitionComplete.set(true);
        setTimeout(() => {
            this.transitionComplete.set(false);
        });
    }

    // Place this method inside your LayoutService class, replacing the existing onWindowResize method.

    onWindowResize() {
        // console.log('onWindowResize: triggered');

        const isCurrentlyDesktop = this.isDesktop();
        const prevIsDesktop = this._currentIsDesktop();
        this._currentIsDesktop.set(isCurrentlyDesktop); // Update current desktop state

        const currentLayoutConfig = this.layoutConfig();
        const currentLayoutState = this.layoutState();

        const currentMenuMode = currentLayoutConfig.menuMode;
        const wasForcedOverlayByZoom = this._forcedOverlayFromStaticDesktop();

        // Define a "narrow desktop" threshold where we want to force to overlay if initially static and collapsed
        const NARROW_DESKTOP_THRESHOLD = 1200; // Adjust this value as needed

        console.log(`onWindowResize: current window.innerWidth = ${window.innerWidth}`);
        console.log(`onWindowResize: isCurrentlyDesktop = ${isCurrentlyDesktop}, prevIsDesktop = ${prevIsDesktop}`);
        console.log(`onWindowResize: currentMenuMode = ${currentMenuMode}, wasForcedOverlayByZoom = ${wasForcedOverlayByZoom}`);
        console.log(`onWindowResize: staticMenuDesktopInactive = ${currentLayoutState.staticMenuDesktopInactive}`);

        // --- PRIMARY LOGIC: Switching between Desktop/Mobile breakpoints (991px) ---
        if (isCurrentlyDesktop !== prevIsDesktop) {
            console.log(`onWindowResize: Primary breakpoint crossed! Transition from ${prevIsDesktop ? 'Desktop' : 'Mobile'} to ${isCurrentlyDesktop ? 'Desktop' : 'Mobile'}.`);

            if (isCurrentlyDesktop) { // Just transitioned from mobile to desktop (window width > 991px)
                //console.log('onWindowResize: Transitioned to Desktop.');
                if (currentMenuMode === 'overlay') {
                    //console.log('onWindowResize: Menu was overlay, reverting to static.');
                    this.layoutConfig.update(prev => ({ ...prev, menuMode: 'static' }));
                    this.layoutState.update(prev => ({ ...prev, staticMenuDesktopInactive: false, overlayMenuActive: false, staticMenuMobileActive: false }));
                } else if (currentMenuMode === 'static' && currentLayoutState.staticMenuMobileActive) {
                    // If it was static on desktop, but was forced to mobile overlay behavior, reactivate static desktop
                    //console.log('onWindowResize: Was static on mobile, reactivating static desktop.');
                    this.layoutState.update(prev => ({ ...prev, staticMenuDesktopInactive: false, staticMenuMobileActive: false }));
                }
                this._forcedOverlayFromStaticDesktop.set(false); // Reset forced flag
                //console.log('onWindowResize: _forcedOverlayFromStaticDesktop reset to false (primary breakpoint).');

            } else { // Just transitioned from desktop to mobile (window width <= 911px)
                //console.log('onWindowResize: Transitioned to Mobile.');
                if (currentMenuMode === 'static') {
                    //console.log('onWindowResize: Menu was static, forcing to overlay for mobile.');
                    this.layoutConfig.update(prev => ({ ...prev, menuMode: 'overlay' }));
                    this.layoutState.update(prev => ({ ...prev, overlayMenuActive: false, staticMenuMobileActive: false, staticMenuDesktopInactive: false }));
                }
                this._forcedOverlayFromStaticDesktop.set(false); // Reset forced flag
                //console.log('onWindowResize: _forcedOverlayFromStaticDesktop reset to false (primary breakpoint).');
            }
        }

        // --- SECONDARY LOGIC: Custom "Sakai-like" behavior for static menu collapse on Desktop ---
        // This applies ONLY if the current mode is 'static' AND we are on desktop.
        // It checks if the static menu is currently inactive (collapsed by user action).

        // Scenario A: Screen narrows within Desktop range AND static menu is currently collapsed
        if (isCurrentlyDesktop && currentMenuMode === 'static' && currentLayoutState.staticMenuDesktopInactive) {
            console.log(`onWindowResize: Desktop, Static mode, Menu is inactive.`);
            if (window.innerWidth <= NARROW_DESKTOP_THRESHOLD && !wasForcedOverlayByZoom) {
                console.log(`onWindowResize: Entering narrow desktop threshold (${NARROW_DESKTOP_THRESHOLD}px) and NOT previously forced. Forcing to overlay.`);
                this.layoutConfig.update(prev => ({ ...prev, menuMode: 'overlay' })); // Temporarily switch to overlay
                this.layoutState.update(prev => ({ ...prev, overlayMenuActive: false })); // Ensure it starts closed
                this._forcedOverlayFromStaticDesktop.set(true); // Mark that it was forced by this zoom logic
            }
        }

        // Scenario B: Screen widens back within Desktop range AND it was previously forced to overlay by zoom
        if (isCurrentlyDesktop && wasForcedOverlayByZoom) {
            console.log(`onWindowResize: Desktop, previously forced to overlay by zoom.`);
            if (window.innerWidth > NARROW_DESKTOP_THRESHOLD) {
                console.log(`onWindowResize: Exiting narrow desktop threshold. Reverting to static and expanding menu.`);
                this.layoutConfig.update(prev => ({ ...prev, menuMode: 'static' })); // Switch back to static
                this.layoutState.update(prev => ({ ...prev, staticMenuDesktopInactive: false })); // And expand it
                this._forcedOverlayFromStaticDesktop.set(false); // Reset the forced flag
            }
        }
        //console.log('onWindowResize: finished. Current Menu Mode:', this.layoutConfig().menuMode);
        //console.log('onWindowResize: Current Layout State:', this.layoutState());
    }

    onMenuToggle() {
        console.log('In MENU TOGGLE!'); // Keep this for debugging if needed

        const layoutConfigValue = this.layoutConfig();
        // const layoutStateValue = this.layoutState(); // REMOVED: This line was unused

        const isDesktopView = this.isDesktop();
        const currentMenuMode = layoutConfigValue.menuMode;

        if (isDesktopView) {
            if (currentMenuMode === 'overlay') {
                this.layoutState.update((prev) => ({ ...prev, overlayMenuActive: !prev.overlayMenuActive }));
                if (this.layoutState().overlayMenuActive) { // Accessing directly, or could use prev.overlayMenuActive if needed after update
                    this.overlayOpen.next(null);
                }
            } else if (currentMenuMode === 'static') {
                this.layoutState.update((prev) => ({ ...prev, staticMenuDesktopInactive: !prev.staticMenuDesktopInactive }));
            }
        } else { // Mobile view always acts like overlay on toggle
            this.layoutState.update((prev) => ({ ...prev, staticMenuMobileActive: !prev.staticMenuMobileActive }));
            if (this.layoutState().staticMenuMobileActive) {
                this.overlayOpen.next(null);
            }
        }
        // When a user manually toggles the menu, they are taking control,
        // so reset the custom "forced overlay by zoom" flag.
        this._forcedOverlayFromStaticDesktop.set(false);
    }

    isDesktop() {
        return window.innerWidth > 991; // This defines your main desktop breakpoint
    }

    isMobile() {
        return !this.isDesktop();
    }

    onConfigUpdate() {
        this._config = { ...this.layoutConfig() };
        this.configUpdate.next(this.layoutConfig());
    }

    onMenuStateChange(event: MenuChangeEvent) {
        this.menuSource.next(event);
    }

    reset() {
        this.resetSource.next(true);
    }
}